import { Dispatch, SetStateAction, useState } from 'react';
import { MatchConfirmedContent } from './MatchConfirmedContent';
import { MatchConfirmingContent } from './MatchConfirmingContent';
import { MatchDeclinedContent } from './MatchDeclinedContent';
import { MatchDecliningContent } from './MatchDecliningContent';
import { MatchNeedHelpContent } from './MatchNeedHelpContent';
import { Modal, ModalCallToAction } from '@platform/components';
import { NameCustomProjectContent } from './NameCustomProjectContent';
import * as Sentry from '@sentry/react';
import { OtherUpdateContent } from './OtherUpdateContent';
import { OutcomeSelectionContent } from './OutcomeSelectionContent';
import { UnresponsiveContent } from './UnresponsiveContent';
import { UpdateConfirmedContent } from './UpdateConfirmedContent';
import { UpdateSelectionContent } from './UpdateSelectionContent';
import { useQueryClient } from '@tanstack/react-query';
import { AssignmentDeclinedReason, SimplifiedUserRole } from '@platform/client/schemas';
import { Discovery, MyProjects, ProjectHub } from '@platform/client';

export enum DiscoveryCallModalStage {
  //Initial stage
  Update_Selection = 'update-selection',

  Outcome_Selection = 'outcome-selection',
  Match_Confirming = 'match-confirming',
  Custom_Naming = 'custom-naming',
  Match_Confirmed = 'match-confirmed',
  Match_Declining = 'match-declining',
  Match_Declined = 'match-declined',
  Match_Need_Help = 'match-need-help',

  Unresponsive = 'unresponsive',
  Other = 'other',
  Update_Confirmed = 'other-confirmed',
}

export enum CompletedOutcomes {
  Accepted = 'accepted',
  Declined = 'declined',
  Need_Help = 'need-help',
}

export enum UpdateType {
  Completed = 'completed',
  // TODO: Where is this used?
  Scheduled = 'scheduled',
  Unresponsive = 'unresponsive',
  Other = 'other',
}

export type DiscoveryModalDataProps = {
  introductionId: number;
  assignmentDiscoveryId: number;
  otherPartyName: string;
  userType: SimplifiedUserRole;
  projectId?: number | null;
  projectName?: string | null;
};

export type DiscoveryModalProps = DiscoveryModalDataProps & {
  onClose: () => void;
};

export const DiscoveryCallUpdateModal = (props: DiscoveryModalProps) => {
  const { assignmentDiscoveryId, otherPartyName, onClose, userType } = props;
  // Tracks whether the workflow mutation is currently ongoing and whether we should show
  // a loading state
  const [isSubmissionSaving, setIsSubmissionSaving] = useState(false);
  const [modalStage, setModalStage] = useState<DiscoveryCallModalStage>(
    DiscoveryCallModalStage.Update_Selection,
  );
  // If set, submitting the discovery caused an error and we display a generic error message
  const [isSubmissionErrored, setIsSubmissionErrored] = useState(false);
  const queryClient = useQueryClient();
  const updateDiscoveryStatus = Discovery.useUpdateDiscoveryCall({
    mutation: {
      onMutate: () => {
        setIsSubmissionSaving(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getCountUserActionItemsQueryKey() });
        setIsSubmissionSaving(false);
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataQueryKey({
            assignment_discovery_id: assignmentDiscoveryId,
          }),
        });
      },
      onError: (error) => {
        Sentry.captureException(error);
        setIsSubmissionSaving(false);
      },
    },
  });

  const [declineReason, setDeclineReason] = useState<AssignmentDeclinedReason | undefined>();
  const [completedOutcome, setCompletedOutcome] = useState<CompletedOutcomes | undefined>();
  const [updateType, setUpdateType] = useState<UpdateType | undefined>();

  const [additionalDetails, setAdditionalDetails] = useState('');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [customName, setCustomName] = useState<string>('');
  let initialEndDate = new Date();
  //Default end date is 3 months in the future
  initialEndDate = new Date(initialEndDate.setDate(initialEndDate.getDate() + 90));
  const [endDate, setEndDate] = useState<Date>(initialEndDate);

  const closeModal = () => {
    setModalStage(DiscoveryCallModalStage.Update_Selection);
    setIsSubmissionErrored(false);
    setAdditionalDetails('');
    setDeclineReason(undefined);
    setCompletedOutcome(undefined);
    setUpdateType(undefined);
    setStartDate(new Date());
    setEndDate(initialEndDate);
    setCustomName('');
    onClose();
  };

  const secondaryCTA = getSecondaryCTA(modalStage, setModalStage, closeModal, setCompletedOutcome);

  return (
    <Modal
      isOpen={true}
      headerDivision='step'
      progress={getProgress(modalStage)}
      onClose={closeModal}
      variant='full-screen'
      title={'Provide a Match Update'}
      primaryCTA={{
        ...getPrimaryCTA(
          userType,
          modalStage,
          setModalStage,
          updateType,
          additionalDetails,
          completedOutcome,
          declineReason,
          closeModal,
          updateDiscoveryStatus,
          startDate,
          endDate,
          customName,
          props,
          setIsSubmissionErrored,
        ),
        isDisabled: () => isSubmissionSaving,
      }}
      secondaryCTA={
        secondaryCTA ? { ...secondaryCTA, isDisabled: () => isSubmissionSaving } : undefined
      }
    >
      {modalStage === DiscoveryCallModalStage.Update_Selection && (
        <UpdateSelectionContent updateType={updateType} setUpdateType={setUpdateType} />
      )}
      {modalStage === DiscoveryCallModalStage.Unresponsive && (
        <UnresponsiveContent
          additionalDetails={additionalDetails}
          setAdditionalDetails={setAdditionalDetails}
          otherPartyName={otherPartyName}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Other && (
        <OtherUpdateContent
          additionalDetails={additionalDetails}
          setAdditionalDetails={setAdditionalDetails}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declining && (
        <MatchDecliningContent
          declineReason={declineReason}
          setDeclineReason={setDeclineReason}
          additionalDetails={additionalDetails}
          setAdditionalDetails={setAdditionalDetails}
          isSaving={isSubmissionSaving}
          userAccountType={userType}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declined && <MatchDeclinedContent />}
      {modalStage === DiscoveryCallModalStage.Match_Confirmed && (
        <MatchConfirmedContent otherPartyName={otherPartyName} userAccountType={userType} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Need_Help && (
        <MatchNeedHelpContent
          additionalDetails={additionalDetails}
          setAdditionalDetails={setAdditionalDetails}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Update_Confirmed && <UpdateConfirmedContent />}
      {modalStage === DiscoveryCallModalStage.Outcome_Selection && (
        <OutcomeSelectionContent
          setCompletedOutcome={setCompletedOutcome}
          otherPartyName={otherPartyName}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Confirming && (
        <MatchConfirmingContent
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Custom_Naming && (
        <NameCustomProjectContent
          name={customName}
          setName={setCustomName}
          isSaving={isSubmissionSaving}
        />
      )}
      {isSubmissionErrored && (
        <div className='md:absolute md:left-0 md:right-0 text-center mt-12 text-error-500'>
          <p>Something went wrong while submitting your update. Please try again later.</p>
          <p>If the problem persists, please contact support.</p>
        </div>
      )}
    </Modal>
  );
};

const getPrimaryCTA = (
  userType: SimplifiedUserRole,
  modalStage: DiscoveryCallModalStage,
  setDiscoveryModalStage: (stage: DiscoveryCallModalStage) => void,
  updateType: UpdateType | undefined,
  additionalDetails: string | undefined,
  completedOutcome: CompletedOutcomes | undefined,
  declineReason: AssignmentDeclinedReason | undefined,
  closeModal: () => void,
  updateDiscoveryStatus: ReturnType<typeof Discovery.useUpdateDiscoveryCall>,
  startDate: Date,
  endDate: Date,
  customName: string,
  modalProps: DiscoveryModalProps,
  setIsSubmissionErrored: Dispatch<SetStateAction<boolean>>,
): ModalCallToAction => {
  const introductionId: number = modalProps.introductionId;
  /** Returns options for mutate() so that we proceed to the correct modal state upon successful submission, and
   * display an error otherwise */
  const getMutateOptions = (
    successStage: DiscoveryCallModalStage,
  ): Parameters<(typeof updateDiscoveryStatus)['mutate']>[1] => {
    return {
      onSuccess: () => {
        setIsSubmissionErrored(false);
        setDiscoveryModalStage(successStage);
      },
      onError: () => {
        setIsSubmissionErrored(true);
      },
    };
  };

  if (modalStage === DiscoveryCallModalStage.Update_Selection) {
    return {
      text: 'Continue',
      callback: () => {
        if (updateType == UpdateType.Completed) {
          setDiscoveryModalStage(DiscoveryCallModalStage.Outcome_Selection);
        } else if (updateType == UpdateType.Unresponsive) {
          setDiscoveryModalStage(DiscoveryCallModalStage.Unresponsive);
        } else if (updateType == UpdateType.Other) {
          setDiscoveryModalStage(DiscoveryCallModalStage.Other);
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Unresponsive) {
    return {
      text: 'Save & continue',
      callback: () => {
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId: introductionId,
              updateType: 'unresponsive',
              additionalDetails: additionalDetails as string,
            },
          },
          getMutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Other) {
    return {
      text: 'Save & continue',
      callback: () => {
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId: introductionId,
              updateType: 'other',
              additionalDetails: additionalDetails as string,
            },
          },
          getMutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Declining) {
    return {
      text: 'Save & continue',
      callback: () => {
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId: introductionId,
              updateType: 'discovery-completed',
              outcome: 'declined',
              reason: declineReason as AssignmentDeclinedReason,
              // For some reason Orval's generated output expects this to be non-null even though
              // this specific variant allows it
              additionalDetails: additionalDetails as string,
            },
          },
          getMutateOptions(DiscoveryCallModalStage.Match_Declined),
        );
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Declined) {
    return {
      text: userType === SimplifiedUserRole.PROFESSIONAL ? 'Close' : 'Find another Experteer',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Confirmed) {
    return {
      text: 'View in Project Hub',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Need_Help) {
    return {
      text: 'Save & continue',
      callback: () => {
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId: introductionId,
              updateType: 'discovery-completed',
              additionalDetails: additionalDetails as string,
              outcome: 'need-help',
            },
          },
          getMutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Update_Confirmed) {
    return {
      text: 'Close',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Outcome_Selection) {
    return {
      text: 'Continue',
      callback: () => {
        if (completedOutcome == CompletedOutcomes.Accepted) {
          // Professionals accepting a Discovery leads to a separate sequence of steps for
          // defining project dates & (if necessary) naming the custom projects.
          // However, Org Reps are only shown a confirmation screen instead as we consider the
          // pros' decision to be binding
          if (userType === SimplifiedUserRole.PROFESSIONAL) {
            setDiscoveryModalStage(DiscoveryCallModalStage.Match_Confirming);
          } else {
            updateDiscoveryStatus.mutate(
              {
                data: {
                  introductionId: introductionId,
                  updateType: 'discovery-completed',
                  outcome: 'accepted-rep',
                },
              },
              getMutateOptions(DiscoveryCallModalStage.Match_Confirmed),
            );
          }
        } else if (completedOutcome == CompletedOutcomes.Declined) {
          setDiscoveryModalStage(DiscoveryCallModalStage.Match_Declining);
        } else if (completedOutcome == CompletedOutcomes.Need_Help) {
          setDiscoveryModalStage(DiscoveryCallModalStage.Match_Need_Help);
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Confirming) {
    return {
      text: 'Save & continue',
      callback: () => {
        if (!modalProps.projectName) {
          //If the project is custom, proceed to name it and create it serverside on completion
          setDiscoveryModalStage(DiscoveryCallModalStage.Custom_Naming);
        } else {
          // Otherwise, submit the acceptance in this step
          const startDateISO = startDate?.toISOString();
          const endDateISO = endDate?.toISOString();
          updateDiscoveryStatus.mutate(
            {
              data: {
                introductionId: introductionId,
                updateType: 'discovery-completed',
                // Note that only professionals can see this specific screen
                outcome: 'accepted-pro',
                // Send up only date component of the string as FastAPI `date` validation requires omitting
                // the time component
                startDate: startDateISO.substring(0, startDateISO.indexOf('T')),
                endDate: endDateISO.substring(0, endDateISO.indexOf('T')),
                projectName: modalProps.projectName,
              },
            },
            getMutateOptions(DiscoveryCallModalStage.Match_Confirmed),
          );
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Custom_Naming) {
    return {
      text: 'Save & continue',
      callback: () => {
        const startDateISO = startDate?.toISOString();
        const endDateISO = endDate?.toISOString();
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId,
              updateType: 'discovery-completed',
              // Note that only professionals can see this specific screen
              outcome: 'accepted-pro',
              // Send up only date component of the string as FastAPI `date` validation requires omitting
              // the time component
              startDate: startDateISO.substring(0, startDateISO.indexOf('T')),
              endDate: endDateISO.substring(0, endDateISO.indexOf('T')),
              projectName: customName,
            },
          },
          getMutateOptions(DiscoveryCallModalStage.Match_Confirmed),
        );
      },
    };
  } else {
    return {
      text: 'Close',
      callback: () => {
        closeModal();
      },
    };
  }
};

const getSecondaryCTA = (
  modalState: DiscoveryCallModalStage,
  setModalState: (stage: DiscoveryCallModalStage) => void,
  closeModal: () => void,
  setCompletedOutcome: Dispatch<SetStateAction<CompletedOutcomes | undefined>>,
): ModalCallToAction | undefined => {
  if (modalState === DiscoveryCallModalStage.Update_Selection) {
    return {
      text: 'Cancel',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Unresponsive) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Other) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Declining) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Declined) {
    return undefined;
  } else if (modalState === DiscoveryCallModalStage.Match_Confirmed) {
    return {
      text: 'Close',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Need_Help) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Outcome_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Outcome_Selection) {
    return {
      text: 'Go back',
      callback: () => {
        setCompletedOutcome(undefined);
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Confirming) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Custom_Naming) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Match_Confirming);
      },
    };
  }
};

const getProgress = (modalState: DiscoveryCallModalStage): number => {
  if (modalState === DiscoveryCallModalStage.Update_Selection) {
    return 10;
  } else if (modalState === DiscoveryCallModalStage.Unresponsive) {
    return 60;
  } else if (modalState === DiscoveryCallModalStage.Other) {
    return 60;
  } else if (modalState === DiscoveryCallModalStage.Match_Declining) {
    return 70;
  } else if (modalState === DiscoveryCallModalStage.Match_Declined) {
    return 100;
  } else if (modalState === DiscoveryCallModalStage.Match_Confirmed) {
    return 100;
  } else if (modalState === DiscoveryCallModalStage.Match_Need_Help) {
    return 60;
  } else if (modalState === DiscoveryCallModalStage.Update_Confirmed) {
    return 100;
  } else if (modalState === DiscoveryCallModalStage.Outcome_Selection) {
    return 30;
  } else if (modalState === DiscoveryCallModalStage.Match_Confirming) {
    return 50;
  } else if (modalState === DiscoveryCallModalStage.Custom_Naming) {
    return 70;
  } else {
    return 20;
  }
};
